import React, { useEffect, useMemo, useState } from "react";
import {
  ScrollText,
  Filter,
  RefreshCw,
  ShieldAlert,
  User2,
  Calendar,
  Trash2,
  LogIn,
  LogOut,
  Power,
  Sparkles,
  ShieldCheck,
  MapPin,
  Download,
  Users2,
  Home,
  Activity,
  Clock,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../auth/AuthContext";
import { AdminLayout } from "../components/AdminLayout";
import { DatePickerUS } from "../components/ui/date-picker-us";
import { fmtUSDateTime, localDateKey } from "../lib/format";
import { useI18n } from "../i18n/I18nContext";

/**
 * Audit log viewer.
 *
 * Two modes:
 *  - `super = false` (default) — endpoint `/audit-log`, tenant admin only.
 *  - `super = true` — endpoint `/super-admin/audit-log`, cross-tenant view
 *    with extra "tenant_id" filter (only useful for super-admin).
 */
export default function AuditLog({ superAdmin = false }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [actions, setActions] = useState([]);
  const [actors, setActors] = useState([]);
  const [summary, setSummary] = useState([]);
  const [weekly, setWeekly] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [filters, setFilters] = useState({
    actor_id: "",
    action: "",
    target_type: "",
    date_from: "",
    date_to: "",
    tenant_id: "",
  });

  async function load() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => {
        if (v) params.set(k === "date_from" ? "from" : k === "date_to" ? "to" : k, v);
      });
      params.set("limit", "200");
      const path = superAdmin ? "/super-admin/audit-log" : "/audit-log";
      const { data } = await api.get(`${path}?${params.toString()}`);
      setItems(Array.isArray(data) ? data : []);
      if (!superAdmin) {
        const [r, ra] = await Promise.all([
          api.get("/audit-log/actions"),
          api.get("/audit-log/actors"),
        ]);
        setActions(Array.isArray(r.data) ? r.data : []);
        setActors(Array.isArray(ra.data) ? ra.data : []);
        const sp = new URLSearchParams();
        if (filters.date_from) sp.set("from", filters.date_from);
        if (filters.date_to) sp.set("to", filters.date_to);
        const rs = await api.get(`/audit-log/activity-summary?${sp.toString()}`);
        setSummary(Array.isArray(rs.data) ? rs.data : []);
        try {
          const ws = await api.get("/audit-log/weekly-summary");
          setWeekly(ws.data || null);
        } catch {
          setWeekly(null);
        }
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha");
    } finally {
      setLoading(false);
    }
  }

  async function exportCsv() {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => {
        if (v) params.set(k === "date_from" ? "from" : k === "date_to" ? "to" : k, v);
      });
      const { data } = await api.get(`/audit-log/export?${params.toString()}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `audit_log_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV exportado");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha ao exportar");
    } finally {
      setExporting(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [superAdmin]);

  const grouped = useMemo(() => groupByDate(items), [items]);

  if (user && !superAdmin && !["admin"].includes(user.role)) {
    return <Forbidden />;
  }
  if (user && superAdmin && !user.is_super_admin) {
    return <Forbidden />;
  }

  const content = (
    <div
      className={superAdmin ? "space-y-6" : "space-y-6"}
      data-testid={superAdmin ? "super-admin-audit-log-page" : "audit-log-page"}
    >
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className={`text-2xl sm:text-3xl font-bold ${superAdmin ? "text-white" : "text-slate-900"} flex items-center gap-2`}>
            <ScrollText size={28} /> Audit Log
          </h1>
          <p className={`text-sm mt-1 ${superAdmin ? "text-slate-400" : "text-slate-600"}`}>
            {superAdmin
              ? "Visualizando todas as ações sensíveis em TODAS as empresas."
              : "Quem fez o quê na sua empresa. Mantemos isto para sua segurança e auditoria."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!superAdmin && (
            <button
              onClick={exportCsv}
              disabled={exporting}
              data-testid="audit-export-csv"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 disabled:opacity-60"
            >
              <Download size={14} /> {exporting ? "Exportando…" : "Exportar CSV"}
            </button>
          )}
          <button
            onClick={load}
            data-testid="audit-refresh"
            className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold ${
              superAdmin
                ? "border border-white/10 bg-white/5 hover:bg-white/10 text-slate-200"
                : "border border-slate-200 bg-white hover:bg-slate-50"
            }`}
          >
            <RefreshCw size={14} /> Atualizar
          </button>
        </div>
      </header>

      {/* Resumo semanal (últimos 7 dias) */}
      {!superAdmin && weekly && (
        <div
          className="grid grid-cols-3 gap-2 sm:gap-3"
          data-testid="weekly-summary"
        >
          {[
            { key: "transfers", label: "Transferências", value: weekly.transfers, color: "text-blue-700", bg: "bg-blue-50 border-blue-200 hover:bg-blue-100", action: "team.transfer" },
            { key: "finished", label: "Casas finalizadas", value: weekly.finished, color: "text-emerald-700", bg: "bg-emerald-50 border-emerald-200 hover:bg-emerald-100", action: "jobs.finish" },
            { key: "reopens", label: "Reaberturas", value: weekly.reopens, color: "text-amber-700", bg: "bg-amber-50 border-amber-200 hover:bg-amber-100", action: "jobs.reopen" },
          ].map((s) => (
            <button
              key={s.key}
              data-testid={`weekly-${s.key}`}
              onClick={() => {
                const from = new Date(Date.now() - 7 * 864e5).toISOString().slice(0, 10);
                setFilters((f) => ({ ...f, action: s.action, date_from: from, date_to: "" }));
                setTimeout(load, 0);
              }}
              className={`text-left rounded-xl border p-3 sm:p-4 transition-colors ${s.bg} ${
                filters.action === s.action ? "ring-2 ring-offset-1 ring-slate-400" : ""
              }`}
            >
              <div className={`text-2xl sm:text-3xl font-extrabold ${s.color}`}>
                {s.value}
              </div>
              <div className="text-[11px] sm:text-xs font-semibold text-slate-500 mt-0.5">
                {s.label}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
        <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold flex items-center gap-1.5">
          <Filter size={12} /> Filtros
        </div>
        {!superAdmin && (
          <div className="flex flex-wrap gap-2">
            <button
              data-testid="quick-filter-transfers"
              onClick={() => {
                setFilters((f) => ({
                  ...f,
                  action: f.action === "team.transfer" ? "" : "team.transfer",
                }));
                setTimeout(load, 0);
              }}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${
                filters.action === "team.transfer"
                  ? "bg-blue-600 text-white border-blue-600"
                  : "bg-white text-blue-700 border-blue-200 hover:bg-blue-50"
              }`}
            >
              {"Só transferências de equipe"}
            </button>
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
          {superAdmin && (
            <Input
              label="Tenant ID"
              value={filters.tenant_id}
              onChange={(v) => setFilters((f) => ({ ...f, tenant_id: v }))}
              placeholder="Ex.: legacy-001"
              testid="filter-tenant"
            />
          )}
          {!superAdmin && (
            <Select
              label="Colaborador"
              value={filters.actor_id}
              onChange={(v) => setFilters((f) => ({ ...f, actor_id: v }))}
              options={[{ value: "", label: "Todos" }].concat(
                actors.map((a) => ({
                  value: a.actor_id,
                  label: a.name || a.email || a.actor_id,
                })),
              )}
              testid="filter-actor"
            />
          )}
          {!superAdmin ? (
            <Select
              label="Ação"
              value={filters.action}
              onChange={(v) => setFilters((f) => ({ ...f, action: v }))}
              options={[{ value: "", label: "Todas" }].concat(
                actions.map((a) => ({ value: a, label: a })),
              )}
              testid="filter-action"
            />
          ) : (
            <Input
              label="Ação"
              value={filters.action}
              onChange={(v) => setFilters((f) => ({ ...f, action: v }))}
              placeholder="Ex.: collaborator.hard_delete"
              testid="filter-action"
            />
          )}
          <Input
            label="Tipo de alvo"
            value={filters.target_type}
            onChange={(v) => setFilters((f) => ({ ...f, target_type: v }))}
            placeholder="user, client, tenant…"
            testid="filter-target-type"
          />
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">De</label>
            <DatePickerUS
              value={filters.date_from}
              onChange={(v) => setFilters((f) => ({ ...f, date_from: v }))}
              testId="filter-date-from"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Até</label>
            <DatePickerUS
              value={filters.date_to}
              onChange={(v) => setFilters((f) => ({ ...f, date_to: v }))}
              testId="filter-date-to"
            />
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={load}
            data-testid="apply-filters"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800"
          >
            Aplicar
          </button>
          <button
            onClick={() => {
              setFilters({
                actor_id: "",
                action: "",
                target_type: "",
                date_from: "",
                date_to: "",
                tenant_id: "",
              });
              setTimeout(load, 0);
            }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 text-sm font-semibold hover:bg-slate-50"
          >
            Limpar
          </button>
        </div>
      </div>

      {/* Resumo de atividade por colaborador (apenas tenant admin) */}
      {!superAdmin && summary.length > 0 && (
        <div
          className="bg-white border border-slate-200 rounded-xl p-4 space-y-3"
          data-testid="activity-summary"
        >
          <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold flex items-center gap-1.5">
            <Users2 size={12} /> Resumo por colaborador
            {(filters.date_from || filters.date_to) && (
              <span className="text-[10px] normal-case font-medium text-slate-400">
                ({filters.date_from || "início"} → {filters.date_to || "hoje"})
              </span>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {summary.slice(0, 12).map((s) => (
              <button
                key={s.actor_id}
                onClick={() => {
                  setFilters((f) => ({ ...f, actor_id: s.actor_id }));
                  setTimeout(load, 0);
                }}
                data-testid={`summary-card-${s.actor_id}`}
                className="text-left rounded-lg border border-slate-200 hover:border-blue-300 hover:bg-blue-50/40 transition p-3"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-900 truncate">
                      {s.name || s.email || "—"}
                    </div>
                    {s.role && (
                      <span className="text-[10px] font-semibold uppercase text-slate-400">
                        {s.role}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-4 mt-2">
                  <span className="inline-flex items-center gap-1 text-sm font-bold text-emerald-700">
                    <Home size={13} /> {s.jobs_finished}
                    <span className="text-[10px] font-medium text-slate-400">casas</span>
                  </span>
                  <span className="inline-flex items-center gap-1 text-sm font-bold text-blue-700">
                    <Activity size={13} /> {s.actions_count}
                    <span className="text-[10px] font-medium text-slate-400">ações</span>
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Timeline */}
      <div className="space-y-6">
        {loading ? (
          <div className="text-center py-10 text-slate-500">Carregando…</div>
        ) : items.length === 0 ? (
          <div className="text-center py-10 text-slate-500 bg-white border border-dashed border-slate-200 rounded-xl">
            Nenhum registro encontrado.
          </div>
        ) : (
          Object.entries(grouped).map(([date, entries]) => (
            <DayGroup key={date} date={date} entries={entries} superAdmin={superAdmin} />
          ))
        )}
      </div>
    </div>
  );

  // Super-admin variant is wrapped externally in <SuperAdminLayout>.
  // Tenant-admin variant needs the regular sidebar to navigate back.
  if (superAdmin) return content;
  return <AdminLayout>{content}</AdminLayout>;
}

// ============================================================================
function Forbidden() {
  return (
    <div className="p-6">
      <div className="max-w-md mx-auto bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <ShieldAlert size={32} className="mx-auto text-red-600 mb-3" />
        <h2 className="text-lg font-semibold text-red-800">Acesso restrito</h2>
        <p className="text-sm text-red-700 mt-1">Esta página exige permissões de admin.</p>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, placeholder, type = "text", testid }) {
  return (
    <label className="block space-y-1">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
      />
    </label>
  );
}

function Select({ label, value, onChange, options, testid }) {
  return (
    <label className="block space-y-1">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 bg-white"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function DayGroup({ date, entries, superAdmin }) {
  // `date` é uma chave YYYY-MM-DD (timezone local). Construímos um Date
  // local para formatar em estilo americano sem deslocar o fuso.
  const dateLabel = useMemo(() => {
    if (!date || date === "—") return date;
    const [y, m, d] = date.split("-").map(Number);
    if (!y) return date;
    const local = new Date(y, m - 1, d, 12, 0, 0);
    return local.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  }, [date]);
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
        <Calendar size={12} /> {dateLabel}
      </div>
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {entries.map((e) => (
          <Entry key={e.id} entry={e} superAdmin={superAdmin} />
        ))}
      </div>
    </div>
  );
}

const ACTION_ICONS = {
  "auth.login": LogIn,
  "auth.login_failed": ShieldAlert,
  "auth.logout": LogOut,
  "collaborator.hard_delete": Trash2,
  "collaborator.deactivate": Trash2,
  "client.delete": Trash2,
  "tenant.signup": Sparkles,
  "tenant.suspended": Power,
  "tenant.active": ShieldCheck,
};

const ACTION_COLORS = {
  "auth.login": "text-emerald-700 bg-emerald-50 border-emerald-200",
  "auth.login_failed": "text-red-700 bg-red-50 border-red-200",
  "collaborator.hard_delete": "text-red-700 bg-red-50 border-red-200",
  "collaborator.deactivate": "text-amber-700 bg-amber-50 border-amber-200",
  "client.delete": "text-red-700 bg-red-50 border-red-200",
  "tenant.signup": "text-blue-700 bg-blue-50 border-blue-200",
  "tenant.suspended": "text-red-700 bg-red-50 border-red-200",
  "tenant.active": "text-emerald-700 bg-emerald-50 border-emerald-200",
};

function Entry({ entry, superAdmin }) {
  const { t } = useI18n();
  const Icon = ACTION_ICONS[entry.action] || User2;
  const palette = ACTION_COLORS[entry.action] || "text-slate-700 bg-slate-50 border-slate-200";
  // Hora estilo americano (12h, fuso LOCAL) — antes usava entry.timestamp.split("T")[1] (UTC).
  const dateTime = fmtUSDateTime(entry.timestamp);
  return (
    <div
      data-testid={`audit-entry-${entry.id}`}
      className={`grid grid-cols-12 gap-3 items-center px-3 py-3 border-b border-slate-100 last:border-0 transition ${
        entry.suspicious
          ? "bg-red-50/70 hover:bg-red-50 border-l-4 border-l-red-500"
          : "hover:bg-slate-50/50"
      }`}
    >
      <div className={`col-span-1 w-9 h-9 rounded-lg border flex items-center justify-center ${palette}`}>
        <Icon size={16} />
      </div>
      <div className="col-span-11 sm:col-span-7 min-w-0">
        <div className="text-sm font-semibold text-slate-900">
          <span className="font-mono text-xs px-1.5 py-0.5 rounded bg-slate-100 mr-2">
            {entry.action}
          </span>
          {entry.target_name || entry.target_id || "—"}
        </div>
        <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap items-center gap-x-1 gap-y-1">
          <span>por <span className="font-semibold text-slate-700">{entry.actor_name || "Sistema"}</span></span>
          {entry.actor_email && (
            <span className="text-slate-400 break-all">· {entry.actor_email}</span>
          )}
          {entry.actor_role && (
            <span className="px-1.5 py-0.5 rounded-full bg-slate-100 text-[10px] font-semibold uppercase">
              {entry.actor_role}
            </span>
          )}
        </div>
        <div
          data-testid={`audit-datetime-${entry.id}`}
          className="text-[11px] text-slate-500 mt-1 flex items-center gap-1 font-medium sm:hidden"
        >
          <Clock size={11} className="text-slate-400 shrink-0" />
          {dateTime}
        </div>
        {entry.metadata && Object.keys(entry.metadata).length > 0 && (
          <div className="text-[11px] text-slate-400 mt-1 truncate">
            {Object.entries(entry.metadata)
              .map(([k, v]) => `${k}=${typeof v === "object" ? JSON.stringify(v) : v}`)
              .join(" · ")}
          </div>
        )}
        {entry.location && (
          <div className="text-[11px] text-slate-500 mt-1 flex items-center gap-1 flex-wrap">
            <MapPin size={11} className={entry.suspicious ? "text-red-500 shrink-0" : entry.location_precise ? "text-emerald-600 shrink-0" : "text-blue-500 shrink-0"} />
            <span className={entry.suspicious ? "font-semibold text-red-700" : "font-medium text-slate-600"}>{entry.location}</span>
            {entry.location_precise && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[9px] font-bold uppercase tracking-wide">GPS</span>
            )}
            {entry.ip && <span className="text-slate-400">· {entry.ip}</span>}
            {entry.suspicious && (
              <span
                data-testid={`audit-suspicious-${entry.id}`}
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 text-[10px] font-bold uppercase tracking-wide"
              >
                <ShieldAlert size={10} /> {t("Novo local de acesso")}
              </span>
            )}
          </div>
        )}
        {superAdmin && entry.tenant_id && (
          <div className="text-[10px] text-blue-600 mt-1 font-mono">
            tenant: {entry.tenant_id}
          </div>
        )}
      </div>
      <div className="hidden sm:block sm:col-span-4 text-right text-xs text-slate-500">
        <div className="font-mono">{dateTime}</div>
        {entry.location ? (
          <div className="text-[10px] text-slate-500 flex items-center justify-end gap-0.5 mt-0.5">
            <MapPin size={10} className="text-blue-500" /> {entry.location}
          </div>
        ) : (
          entry.ip && <div className="text-[10px] text-slate-400">{entry.ip}</div>
        )}
      </div>
    </div>
  );
}

function groupByDate(items) {
  const out = {};
  for (const it of items) {
    // Usa data no fuso LOCAL — antes usava UTC (it.timestamp.split("T")[0])
    // o que deslocava a data em 1 dia para usuários a oeste de UTC.
    const day = localDateKey(it.timestamp);
    if (!out[day]) out[day] = [];
    out[day].push(it);
  }
  return out;
}
